<template>
	<div click.stop>
		<div class="modal is-active">
			<div class="modal-background" @click="cancel"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">
						{{ localUser.id ? "Editera" : "Ny" }} anställd
					</p>
					<button class="delete" aria-label="close" @click="cancel"></button>
				</header>
				<section class="modal-card-body no-scroll">
					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">Förnamn</label>
							<div class="control">
								<input
									:disabled="!this.$isAllowedTo('users_edit')"
									type="text"
									v-model="localUser.firstName"
									class="input"
									placeholder="Förnamn"
									autocomplete="off"
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">Efternamn</label>
							<div class="control">
								<input
									:disabled="!this.$isAllowedTo('users_edit')"
									type="text"
									v-model="localUser.lastName"
									class="input"
									placeholder="Efternamn"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>

					<div class="columns is-mobile">
						<div class="field column">
							<label class="label">Email</label>
							<div
								class="control"
								:class="{
									'is-disabled': localUser.email && !validUserEmail
								}"
							>
								<input
									:disabled="localUser.id || !this.$isAllowedTo('users_edit')"
									type="email"
									v-model="localUser.email"
									class="input"
									placeholder="Email"
									autocomplete="off"
								/>
								<span
									class="icon"
									id="invalid-email-icon"
									v-if="localUser.email && !validUserEmail"
								>
									<i class="fas fa-exclamation-circle" />
								</span>
							</div>
						</div>
						<div class="field column">
							<label class="label">Telefon</label>
							<div class="control">
								<input
									:disabled="!this.$isAllowedTo('users_edit')"
									type="text"
									v-model="localUser.phoneNumber"
									class="input"
									placeholder="Telefon"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>

					<div v-if="!localUser.id" class="columns is-mobile">
						<div class="field column">
							<label class="label">Lösenord</label>
							<div class="control">
								<input
									type="password"
									v-model="password"
									class="input"
									placeholder="Lösenord"
									autocomplete="off"
								/>
							</div>
						</div>
						<div class="field column">
							<label class="label">Upprepa lösenord</label>
							<div class="control">
								<input
									type="password"
									v-model="repeatPassword"
									class="input"
									:class="password != repeatPassword ? 'is-danger' : ''"
									placeholder="Lösenord"
									autocomplete="off"
								/>
							</div>
						</div>
					</div>
					<div class="field" style="margin-top: 12px">
						<label class="label">Roller</label>
						<div class="control">
							<v-select
								:disabled="!this.$isAllowedTo('users_edit')"
								class="panel-select user"
								v-model="selectedRoles"
								:multiple="true"
								:options="roles"
								:placeholder="'Välj roller'"
								:allowEmpty="true"
								:internalSearch="true"
								:showLabels="false"
								:hideSelected="true"
								:closeOnSelect="false"
								trackBy="id"
								label="name"
							>
								<span slot="noResult">Inga resultat</span>
								<span
									slot="caret"
									slot-scope="{ toggle }"
									class="caret"
									@mousedown.prevent="toggle()"
								><i class="fas fa-caret-down caret-inner"></i
								></span>
								<span
									slot="tag"
									slot-scope="{ option, remove }"
									@mousedown.prevent
								><span class="custom__tag">
									{{ option.name
									}}<span
										class="custom__remove"
										@click.prevent="remove(option)"
									>
										<i class="fas fa-times fa-sm"></i></span
									></span>
								</span>
							</v-select>
						</div>
					</div>
					<div class="field" style="margin-top: 12px" v-if="externalRoles?.length > 0 || selectedExternalRoles?.length > 0">
						<label class="label">Externa roller</label>
						<div class="control">
							<v-select
								:disabled="!this.$isAllowedTo('users_manage_external_roles')"
								class="panel-select user"
								v-model="selectedExternalRoles"
								:multiple="true"
								:options="externalRoles"
								:placeholder="'Välj roller'"
								:allowEmpty="true"
								:internalSearch="true"
								:showLabels="false"
								:hideSelected="true"
								:closeOnSelect="false"
								trackBy="id"
								label="name"
							>
								<span slot="noResult">Inga resultat</span>
								<span
									slot="caret"
									slot-scope="{ toggle }"
									class="caret"
									@mousedown.prevent="toggle()"
								><i class="fas fa-caret-down caret-inner"></i
								></span>
								<span
									slot="tag"
									slot-scope="{ option, remove }"
									@mousedown.prevent
								><span class="custom__tag">
									{{ option.name
									}}<span
										class="custom__remove"
										@click.prevent="remove(option)"
									>
										<i class="fas fa-times fa-sm"></i></span
									></span>
								</span>
							</v-select>
						</div>
					</div>
					<div>
						<span v-if="error" class="tag is-danger is-medium is-light mt-2">{{
							error
						}}</span>
					</div>
					<p v-if="successFeedback" class="has-text-success is-medium mt-5">
						{{ successFeedback }}
					</p>
				</section>
				<footer class="modal-card-foot">
					<div class="column flex">
						<button
							class="button is-dark"
							:class="isLoading ? 'is-loading' : ''"
							:disabled="disableSave"
							@click="onSubmit"
						>
							<span class="icon is-info"><i class="fas fa-save"/></span
							><span>Spara</span>
						</button>
						<button
							v-if="localUser.id"
							class="button is-dark"
							id="reset-pwd-button"
							@click="resetPassword"
						>
							<span class="icon is-info"><i class="fas fa-lock"/></span>
							<p>Återställ lösenord</p>
						</button>
						<div class="select" style="text-align: right; float: right">
							<select v-model="localUser.userRole" :disabled="currentUserMissingPrivilege">
								<option v-for="role in claimRoles" :value="getAccessLevel(role)" :key="role">{{ getAccessLevelName(role) }}</option>
								<option v-if="currentUserMissingPrivilege" selected>{{ getAccessLevelName(3) }}</option>
							</select>
						</div>
					</div>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

const initialUserState = () => ({
	firstName: '',
	lastName: '',
	email: '',
	phoneNumber: '',
	userRole: 'User',
	roleIds: [],
	externalRoleIds: []
});

export default {
	props: {
		user: {
			type: Object,
			default: () => ({})
		}
	},

	data: () => ({
		roles: [],
		externalRoles: [],
		claimRoles: [],

		localUser: initialUserState(),

		selectedRoles: [],
		selectedExternalRoles: [],
		password: '',
		repeatPassword: '',

		isLoading: false,
		error: '',
		successFeedback: ''
	}),

	computed: {
		...mapGetters('user', ['getActiveCustomer']),

		validPassword() {
			if (this.localUser.id) {
				return true;
			} else {
				return this.password != '' && this.password == this.repeatPassword;
			}
		},

		disableSave() {
			return (
				this.localUser.firstName == '' ||
				this.localUser.lastName == '' ||
				!this.validPassword ||
				this.localUser.phoneNumber == '' ||
				this.localUser.email == '' ||
				!this.validUserEmail ||
				this.currentUserMissingPrivilege
			);
		},

		currentUserMissingPrivilege() {
			return !this.claimRoleNames.some(x => x === this.localUser.userRole);
		},

		validUserEmail() {
			return this.validEmail(this.localUser.email);
		},

		claimRoleNames() {
			return this.claimRoles.map(x => this.getAccessLevel(x));
		}
	},

	async created() {
		this.localUser = {
			...this.localUser,
			...this.user
		};

		if (this.user.id) {
			await this.fetchUser(this.user.id);
		}

		await this.fetchRoles();

		this.selectedRoles = this.localUser?.roles?.filter(x => x.customerId === this.getActiveCustomer.id) ?? [];

		let claimRoles = await this.$services.user.getAvailableRolesForUser({
			loading: val => this.isLoading = val
		});

		this.claimRoles = claimRoles;

		if (this.user.id) {
			try {
				this.externalRoles = await this.$services.role.getExternalRoles(this.user.id, this.getActiveCustomer.id, {
					loading: val => this.isLoading = val
				});

				this.selectedExternalRoles = this.externalRoles?.filter(x => this.localUser.roles.some(y => y.id === x.id)) ?? [];
			} catch (error) {
				console.error(error);
			}
		}
	},

	methods: {
		onSubmit() {
			this.successFeedback = '';
			this.error = '';

			if (this.localUser.id) {
				this.updateUser();

				return;
			}

			this.createUser();
		},

		cancel() {
			this.$emit('cancel');
		},

		async fetchRoles() {
			this.roles = await this.$services.role.list({
				customerId: this.getActiveCustomer?.id,
				pageSize: 0
			});
		},

		async createUser() {
			try {
				await this.$services.user.create(
					{
						email: this.localUser.email,
						userName: this.localUser.email,
						firstName: this.localUser.firstName,
						lastName: this.localUser.lastName,
						emailConfirmed: true,
						password: this.password,
						applicationId: 'string',
						phoneNumber: this.localUser.phoneNumber,
						companyId: this.getActiveCustomer.companyId,
						customerId: this.getActiveCustomer?.id,
						userRole: this.localUser.userRole,
						country: 'Swe',
						roleIds: [...this.selectedRoles.map(r => r.id), ...this.selectedExternalRoles.map(x => x.id)]
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);

				this.successFeedback = 'Användaren skapades upp utan problem.';
				this.localUser = initialUserState();
				this.password = '';
				this.repeatPassword = '';
				this.$emit('on-update');
			} catch (error) {
				this.error = error || 'Något gick fel vid uppskapande av användare.';
			}
		},

		async updateUser() {
			try {
				await this.$services.user.update(
					this.localUser.id,
					{
						email: this.localUser.email,
						userName: this.localUser.email,
						firstName: this.localUser.firstName,
						lastName: this.localUser.lastName,
						phoneNumber: this.localUser.phoneNumber,
						companyId: this.localUser.companyId,
						customerId: this.localUser.customerId,
						identityId: this.localUser.identityId,
						roleIds: [...this.selectedRoles.map(r => r.id), ...this.selectedExternalRoles.map(x => x.id)]
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);

				await this.$services.user.updateRoles(
					this.localUser.id,
					{
						role: this.localUser.userRole
					},
					{
						loading: val => {
							this.isLoading = val;
						}
					}
				);

				this.successFeedback = 'Användaren updaterades utan problem.';
				this.$emit('on-update');
			} catch (error) {
				this.error = error || 'Något gick fel vid uppdateraing av användare.';
			}
		},

		async resetPassword() {
			this.error = '';
			
			await this.$services.user.resetPassword({
				email: this.localUser.email
			});

			this.successFeedback = 'Ett mail kommer inom kort till din inkorg med länk för att återställa ditt lösenord.';
		},

		async fetchUser(userId) {
			try {
				const data = await this.$services.user.get(userId);
				this.localUser = {
					...this.localUser,
					...data
				};
			} catch (error) {
				this.error = error;
			}
		},

		validEmail(email) {
			// eslint-disable-next-line no-useless-escape
			return /[a-zA-zåäöåÄÖ0-9.-]+@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.column.flex {
  display: flex;
}
.email {
  margin-top: 0.5rem;
  color: white;
}

.multiselect.panel-select.user {
  height: auto;
  max-width: none;

  .custom__tag {
    display: inline-block;
    margin-bottom: 5px;
  }
}

#reset-pwd-button {
  > p {
    padding-top: 4px !important;
  }
}

.field {
  .control {
    &.is-disabled {
      input {
        border: 1px solid $danger;
        box-shadow: inset 0 0 5px rgba($color: $info, $alpha: 0.8);
      }
    }

    #invalid-email-icon {
      height: fit-content;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%) !important;
      right: 1rem;

      > i {
        color: $danger;
      }
    }
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.user .multiselect__tags {
  white-space: normal;
}
</style>
